/**
 * Word lists backing `username.ts`'s validation. Split out so the module
 * logic and the (necessarily blunt) data it screens against don't fight for
 * attention in the same file.
 *
 * `BLOCKED_SUBSTRINGS` is matched as a **substring** of the normalized name
 * (see `normalizeForScreening` — lowercase, `-`/`_` stripped, common
 * leetspeak folded), so every entry here must already be in that normalized
 * form: lowercase, no separators, digits/`@`/`$` folded to letters.
 *
 * Substring matching is inherently the "Scunthorpe problem" — a short,
 * otherwise-common string blocks any name that happens to contain it. This
 * list is deliberately conservative about that: short profanity that shows up
 * inside ordinary words or names is either left out entirely or only included
 * as part of a longer, unambiguous compound. Notably excluded despite being
 * real slurs or profanity, and why:
 *   - "ass", "hell", "cock", "dick" — common inside plain words (classy,
 *     grasshopper, shell, cockpit, Hancock, Dickinson) and surnames; only the
 *     longer compounds ("asshole", "dickhead", ...) are listed.
 *   - "cunt" — the canonical Scunthorpe case; "cuntface"/"cunthole" cover the
 *     deliberate uses without catching the town (or names built the same way).
 *   - "coon" — collides with "raccoon"; "dyke" — collides with "Van Dyke" /
 *     "Dykstra"; "spic" — collides with "spice"/"hospice". All three are real
 *     slurs with no safe longer form, so they're left unfiltered here rather
 *     than block common words and surnames; the reserved/impersonation list
 *     and any future human moderation are the backstop.
 *   - "spade" is not a slur candidate at all in this list — it is a suit in
 *     the game itself and must never be blocked.
 *
 * `RESERVED_NAMES` is exact-match (see `validateUsername`, which compares the
 * lowercased-but-otherwise-unmodified name) rather than substring, so it can
 * safely be broader: impersonation handles that would be confusing to see
 * claimed by an ordinary player.
 */

export const BLOCKED_SUBSTRINGS: string[] = [
  // General profanity
  "fuck",
  "fucker",
  "fucking",
  "motherfucker",
  "shit",
  "shithead",
  "shitbag",
  "bullshit",
  "horseshit",
  "asshole",
  "assclown",
  "dumbass",
  "jackass",
  "dipshit",
  "bitch",
  "bastard",
  "bollocks",
  "wanker",
  "douchebag",
  "cuntface",
  "cunthole",
  "twat",
  "whore",
  "slut",

  // Slurs — racial
  "nigger",
  "nigga",
  "chink",
  "gook",
  "wetback",
  "kike",

  // Slurs — homophobic
  "faggot",
  "tranny",

  // Slurs / abuse — ableist
  "retard",
  "spastic",
];

/** Exact-match impersonation handles, compared case-insensitively. */
export const RESERVED_NAMES: string[] = [
  "admin",
  "administrator",
  "moderator",
  "mod",
  "system",
  "server",
  "official",
  "support",
  "staff",
  "fiveout",
  "five-out",
  "root",
  "owner",
  "dev",
  "developer",
];
